/**
 * haiku-footer — pick the footer photograph.
 *
 * WHY THIS IS CLIENT-SIDE. Shopify caches rendered pages, so a Liquid-side
 * random pick freezes on whichever image happened to be cached and stops being
 * random. Choosing here means a genuinely different photograph per page load.
 *
 * Two sources, in order:
 *   1. config.apiUrl — the owner's gallery. Since 2026-10-24 that is Pop's
 *      public album feed, photos.jurele.lt/album/gallery/photos.json — the same
 *      feed jurele.lt's photography page reads (trlmkb/jrl src/lib/photos.ts):
 *      `{ version: 1, album, photos: [{ id, width, height, thumbUrl,
 *      displayUrl, caption, … }] }`. Also accepted: an array of URL strings or
 *      of objects carrying `url`, or `{ images | photos | data: [...] }`.
 *   2. otherwise the pool the section rendered, from a collection.
 *
 * The section already server-rendered a photograph into the <img>, so this
 * only ever swaps a src. If the fetch fails (down, CORS-blocked, malformed),
 * or this script never runs, that first photograph stays — the footer is never
 * empty.
 *
 * THE FEED IS FETCHED ONCE PER VISIT, AND ONLY AS THE FOOTER APPROACHES. It is
 * the whole album (~175 KB for 346 photos), so it is not worth a request on a
 * page nobody scrolls to the bottom of; the normalised list is kept in
 * sessionStorage so the next page draws from it without asking again.
 *
 * PORTRAIT ONLY. The footer is a portrait frame; a landscape photo shrinks to a
 * strip inside it. Entries that say their size and are not taller than wide
 * are left out (311 of 346 in the gallery are portrait; 15 are square, and
 * squares slipped through until 2026-10-25).
 *
 * THE 400px RENDITION, NOT 2048. Pop keeps two: thumbUrl (400 px wide webp,
 * ~35 KB) and displayUrl (2048 px, ~1 MB). The photo shows ~190–275 CSS px
 * wide, so 400 is sharp at 1x and slightly soft on a retina screen; 2048
 * would be sharp everywhere at thirty times the weight. `apiSize: 'display'`
 * in the section's config switches it.
 */
(function () {
  'use strict';

  var cfg = window.haikuFooterConfig || {};
  if (cfg.enabled === false) return;

  var CACHE_KEY = 'haiku:footer-photos';

  function pick(list) {
    if (!list || !list.length) return null;
    return list[Math.floor(Math.random() * list.length)];
  }

  /* Accepts the shapes an image API is likely to return, so wiring the real
     one is a URL change rather than a code change. */
  function normalise(data) {
    var arr = Array.isArray(data)
      ? data
      : (data && (data.images || data.photos || data.data)) || [];

    if (!Array.isArray(arr)) return [];

    var big = cfg.apiSize === 'display';

    return arr
      .map(function (item) {
        if (typeof item === 'string') return { url: item, alt: '' };
        if (item && typeof item === 'object') {
          // A size that is stated and not portrait is not for this frame.
          if (typeof item.width === 'number' && typeof item.height === 'number' && item.width >= item.height) return null;
          var url = item.url || item.src || item.image || item.href ||
            (big ? item.displayUrl || item.thumbUrl : item.thumbUrl || item.displayUrl);
          if (url) {
            return {
              url: url,
              alt: item.alt || item.title || item.caption || '',
              width: typeof item.width === 'number' ? item.width : null,
              height: typeof item.height === 'number' ? item.height : null
            };
          }
        }
        return null;
      })
      .filter(Boolean);
  }

  function apply(img, choice, onShown) {
    if (!choice || !choice.url) return;

    // Only swap once the replacement has actually decoded, so the footer never
    // flashes empty between the rendered photograph and the chosen one.
    var next = new Image();
    next.decoding = 'async';
    next.onload = function () {
      // The rendered photograph's width/height attributes describe a different
      // picture; state the new one's so the frame takes its proportions.
      var w = choice.width || next.naturalWidth;
      var h = choice.height || next.naturalHeight;
      if (w && h) { img.setAttribute('width', String(w)); img.setAttribute('height', String(h)); }
      img.src = choice.url;
      img.alt = choice.alt || '';
      img.setAttribute('data-hf-picked', '1');
      if (onShown) onShown();
    };
    next.onerror = function () { /* keep the server-rendered photograph */ };
    next.src = choice.url;
  }

  function localPool(root) {
    var el = root.querySelector('[data-hf-pool]');
    if (!el) return [];
    try {
      return normalise(JSON.parse(el.textContent));
    } catch (e) {
      return [];
    }
  }

  function cached() {
    try {
      var raw = window.sessionStorage.getItem(CACHE_KEY);
      if (!raw) return null;
      var c = JSON.parse(raw);
      return c && c.src === cfg.apiUrl && Array.isArray(c.list) && c.list.length ? c.list : null;
    } catch (e) {
      return null;
    }
  }

  function remember(list) {
    try {
      window.sessionStorage.setItem(CACHE_KEY, JSON.stringify({ src: cfg.apiUrl, list: list }));
    } catch (e) { /* private mode or full: the next page fetches again */ }
  }

  /* THE FOOTNOTE (2026-10-24): "from my personal photography gallery", shown
     only once a gallery photo is actually on screen — never on the shop-photo
     fallback — and placed by measuring the photo. Under it when the frame
     leaves room (a phone: ~100px below a width-limited portrait); up its right
     edge, reading bottom to top, when it does not (desktop: the photo is the
     frame's full height, with the free space at the sides). Re-placed on
     resize. */
  function placeNote(img, note) {
    var fig = img.parentNode;
    if (!fig || !fig.getBoundingClientRect) return;
    var fr = fig.getBoundingClientRect();
    var ir = img.getBoundingClientRect();
    if (!ir.width) return;
    var below = fr.bottom - ir.bottom;
    var side = below < 28;
    note.classList.toggle('is-side', side);
    note.style.left = (side ? ir.right - fr.left + 12 : ir.left - fr.left + ir.width / 2) + 'px';
    note.style.top = (side ? ir.bottom - fr.top : ir.bottom - fr.top + 10) + 'px';
  }

  function showNote(root, img) {
    var note = root.querySelector('[data-hf-note]');
    if (!note) return;
    note.hidden = false;
    placeNote(img, note);
    var raf = 0;
    window.addEventListener('resize', function () {
      if (raf) return;
      raf = requestAnimationFrame(function () { raf = 0; placeNote(img, note); });
    }, { passive: true });
  }

  function fromApi(root, img) {
    var onGallery = function () { showNote(root, img); };
    var list = cached();
    if (list) { apply(img, pick(list), onGallery); return; }

    fetch(cfg.apiUrl, { headers: { Accept: 'application/json' } })
      .then(function (r) {
        if (!r.ok) throw new Error('HTTP ' + r.status);
        return r.json();
      })
      .then(function (data) {
        var got = normalise(data);
        if (got.length) {
          remember(got);
          apply(img, pick(got), onGallery);
          return;
        }
        apply(img, pick(localPool(root)));
      })
      .catch(function () {
        // Feed down or CORS-blocked — fall back to the rendered pool.
        apply(img, pick(localPool(root)));
      });
  }

  /* Run `fn` once the footer is within a screen or so of the viewport. */
  function whenNear(root, fn) {
    if (!('IntersectionObserver' in window)) { fn(); return; }
    var io = new IntersectionObserver(function (entries) {
      for (var i = 0; i < entries.length; i++) {
        if (entries[i].isIntersecting) { io.disconnect(); fn(); return; }
      }
    }, { rootMargin: '0px 0px 120% 0px' });
    io.observe(root);
  }

  function boot() {
    var root = document.querySelector('[data-haiku-footer]');
    if (!root) return;

    var img = root.querySelector('[data-hf-photo]');
    if (!img) return;

    if (cfg.apiUrl) {
      whenNear(root, function () { fromApi(root, img); });
      return;
    }

    apply(img, pick(localPool(root)));
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
}());
